import React from 'react';
import { AllocationRule } from 'further-types/lp/allocations';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Controller, useFormContext } from 'react-hook-form';
import { EqualisationFormValues } from './FormWrapper';
import {
  numberToCurrencyString,
  pluralize,
  numberToPercentageString,
  CalendarDay,
} from 'further-ui/utils';
import useEqualiseActions from '../../hooks/useEqualiseActions';
import EqualisationsAllocationsTable from 'components/EqualisationElements/EqualisationsAllocationsTable';
import { Column as TableColumn } from 'further-ui/components/Table';
import NumberInput from 'further-ui/components/NumberInput';

type EqualisationAllocationsFormProps = {
  fields: EqualisationFormValues['actions'][number]['newAllocations'];
  inputAmountLcy: number;
  isNotEditable: boolean;
  allocationRule: AllocationRule;
  actionIndex: number;
  allocationType: 'originalAllocations' | 'newAllocations';
  linkedActionIndex?: number;
  transactionCurrency: CurrencyCode;
};

const EqualisationAllocationsForm: React.FC<
  EqualisationAllocationsFormProps
> = ({
  fields,
  allocationRule,
  inputAmountLcy,
  isNotEditable,
  actionIndex,
  allocationType,
  linkedActionIndex,
  transactionCurrency,
}) => {
  const { control } = useFormContext<EqualisationFormValues>();

  const {
    watchedFields,
    isPending,
    allocationsPath,
    totalAllocationAmountLcy,
    totalAllocationPercentage,
    excludedCommitmentNames,
    onChangeAllocationRule,
    handleAllocationAmountChange,
    handleAllocationPercentageChange,
    handleExcludeFromAllocationChange,
    isLinkedAction,
  } = useEqualiseActions({
    actionIndex,
    linkedActionIndex,
    allocationType,
    inputAmountLcy,
  });

  const investorCount = Array.isArray(watchedFields) ? watchedFields.length : 0;

  const rows = fields.map((field) => ({
    ...field,
    id: field.commitmentId,
  }));

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'investor',
      label: 'Investor',
      render: (row) => row.investorLabel,
      footer: `Total from ${investorCount} ${pluralize(
        'investor',
        investorCount,
      )}`,
    },
    {
      key: 'totalCommitmentLcy',
      label: 'Total commitment',
      render: (row) => numberToCurrencyString(row.totalCommitmentLcy),
    },
    {
      key: 'unallocatedCommitmentLcy',
      label: 'Unallocated commitment',
      render: (row) => numberToCurrencyString(row.unallocatedCommitmentLcy),
    },
    {
      key: 'closeDate',
      label: 'Close date',
      render: (row) => new CalendarDay(row.closeDate).toReadable(),
    },
    {
      key: 'allocationAmountLcy',
      label: 'Allocation allocated',
      render: (row, rowIndex) =>
        isNotEditable ? (
          <NumberInput
            fixedDecimalScale
            decimals={2}
            value={row.allocationAmountLcy}
            currencyStartAdornment
            currency={transactionCurrency}
            disabled
          />
        ) : (
          <Controller
            control={control}
            name={`${allocationsPath}.${rowIndex}.allocationAmountLcy`}
            render={({ field }) => (
              <NumberInput
                fixedDecimalScale
                decimals={2}
                onChange={(value) => {
                  handleAllocationAmountChange(value ?? 0, rowIndex);
                }}
                value={field.value}
                currencyStartAdornment
                currency={transactionCurrency}
                clearZeroValueOnFocus
                disabled={row.excludeFromAllocation || isPending}
              />
            )}
          />
        ),
      footer: numberToCurrencyString(totalAllocationAmountLcy, {
        currency: transactionCurrency,
      }),
    },
    {
      key: 'allocationPercentage',
      label: 'Allocation allocated (%)',
      render: (row, rowIndex) =>
        isNotEditable ? (
          <NumberInput
            fixedDecimalScale
            decimals={5}
            value={row.allocationPercentage}
            percentageEndAdornment
            disabled
          />
        ) : (
          <Controller
            control={control}
            name={`${allocationsPath}.${rowIndex}.allocationPercentage`}
            render={({ field }) => (
              <NumberInput
                fixedDecimalScale
                decimals={5}
                onChange={(value) => {
                  handleAllocationPercentageChange(value ?? 0, rowIndex);
                }}
                value={field.value}
                percentageEndAdornment
                clearZeroValueOnFocus
                disabled={row.excludeFromAllocation || isPending}
              />
            )}
          />
        ),
      footer: numberToPercentageString(totalAllocationPercentage, {
        accuracy: 5,
        noMultiply: true,
        forceDecimals: true,
      }),
    },
  ];

  return (
    <EqualisationsAllocationsTable
      columns={columns}
      rows={rows}
      allocationRule={allocationRule}
      checkboxes={{
        isCheckedRow: (row) => !row.excludeFromAllocation,
        isDisabledRow: () => isNotEditable || isPending,
        onRowCheckedToggle: (row, rowIndex) => {
          handleExcludeFromAllocationChange(
            !row.excludeFromAllocation,
            rowIndex,
            allocationRule,
          );
        },
      }}
      allocationRuleOptions={[
        {
          value: AllocationRule.ByCommitmentAndClosingDate,
          label: `By commitment and closing date${
            excludedCommitmentNames
              ? ` excluding ${excludedCommitmentNames}`
              : ''
          }`,
        },
        {
          value: AllocationRule.Manual,
          label: 'Manual allocation',
        },
        ...(isLinkedAction
          ? [
              {
                value: AllocationRule.LinkedAction,
                label: 'Linked action',
              },
            ]
          : []),
      ]}
      onChangeAllocationRule={onChangeAllocationRule}
      isNotEditable={isNotEditable}
    />
  );
};

export default EqualisationAllocationsForm;
